import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Skeleton, Alert, Grid } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { fetchSessionTeaser, SessionTeaser, LockedPanel } from '../../services/monetizationServices';

/**
 * The free diagnostic result and its upsell (#08).
 *
 * The paywall itself was already reachable — SessionReviewPanel renders a locked
 * state off a 402 — but the diagnostic *hook* was not: `weakestSkill` and the
 * one-line pitch the API returns had no consumer, so a student saw a score and no
 * reason to want anything more. That named weakness is the point of the funnel,
 * which is why it is the largest thing on this card.
 *
 * Each locked panel carries its own SKU through to checkout, so the CTA a student
 * presses buys the thing that panel described rather than a generic upgrade.
 */

const PanelCard: React.FC<{ panel: LockedPanel; onUnlock: (sku: string) => void }> = ({ panel, onUnlock }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {panel.locked && <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{panel.title}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {panel.description}
      </Typography>
      {/* Decorative only. No locked content is sent to the client to be un-blurred —
          the server does not return it until the entitlement exists. */}
      <Box aria-hidden sx={{ filter: 'blur(4px)', userSelect: 'none', opacity: 0.55, mb: 2 }}>
        <Typography variant="body2">••••• •••• ••••••• ••• •••••</Typography>
        <Typography variant="body2">•••• ••••••• •• ••••</Typography>
      </Box>
      {panel.locked && (
        <Button fullWidth variant="contained" onClick={() => onUnlock(panel.sku)}>
          Unlock
        </Button>
      )}
    </CardContent>
  </Card>
);

const DiagnosticTeaser: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'unauthorized'>('loading');
  const [teaser, setTeaser] = useState<SessionTeaser | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId) { setState('error'); return; }
    (async () => {
      const result = await fetchSessionTeaser(sessionId);
      if (cancelled) return;
      if (result.state === 'ready') { setTeaser(result.teaser); setState('ready'); }
      else setState(result.state);
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (state === 'loading') return <Skeleton variant="rounded" height={220} sx={{ my: 2 }} />;
  if (state === 'unauthorized') {
    return <Alert severity="info" sx={{ my: 2 }}>Sign in to see your result summary.</Alert>;
  }
  if (state === 'error' || !teaser) {
    return <Alert severity="warning" sx={{ my: 2 }}>We could not load your result summary just now.</Alert>;
  }

  const onUnlock = (sku: string) => navigate(`/checkout?sku=${encodeURIComponent(sku)}`);

  return (
    <Box sx={{ my: 3 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Your result</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {teaser.score}/{teaser.totalScore}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              ({Math.round(teaser.percentage)}%)
            </Typography>
            <Chip size="small" sx={{ ml: 1 }}
                  label={`${teaser.questionsCorrect} of ${teaser.questionsTotal} correct`} />
          </Box>

          {teaser.weakestSkill && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {teaser.hook || `Your weakest area is ${teaser.weakestSkill}.`}
              </Typography>
              {teaser.weakestSkillAccuracyPct != null && (
                <Typography variant="body2" color="text.secondary">
                  You got {Math.round(teaser.weakestSkillAccuracyPct)}% of {teaser.weakestSkill} questions right.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {teaser.lockedPanels.length > 0 && (
        <Grid container spacing={2}>
          {teaser.lockedPanels.map((panel) => (
            <Grid size={{ xs: 12, sm: 6 }} key={panel.key}>
              <PanelCard panel={panel} onUnlock={onUnlock} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DiagnosticTeaser;
